import { FormEvent, ReactNode, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Eye, EyeOff, LockKeyhole, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { clearAdminToken, getAuthStatus, isAdminTokenRemembered, readAdminToken, saveAdminToken } from "../api/client";
import { Button } from "./ui/button";
import { Input } from "./ui/input";

export function AuthGate({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [token, setToken] = useState(readAdminToken);
  const [rememberToken, setRememberToken] = useState(isAdminTokenRemembered);
  const [showToken, setShowToken] = useState(false);
  const [attempted, setAttempted] = useState(false);
  const auth = useQuery({ queryKey: ["auth-status"], queryFn: getAuthStatus, retry: false });

  const locked = auth.data?.auth_required && !auth.data.authenticated;
  const modeLabel = useMemo(() => {
    if (!auth.data) return "检查中";
    if (auth.data.mode === "admin-token") return "Admin Token";
    if (auth.data.mode === "open-key") return "Open Key";
    if (auth.data.mode === "open") return "本地开放模式";
    return "需要访问密钥";
  }, [auth.data]);

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!token.trim()) {
      toast.error("请输入访问密钥");
      return;
    }
    saveAdminToken(token, rememberToken);
    setAttempted(true);
    void queryClient.invalidateQueries({ queryKey: ["auth-status"] });
  }

  function resetToken() {
    clearAdminToken();
    setToken("");
    setRememberToken(false);
    setShowToken(false);
    setAttempted(false);
    void queryClient.invalidateQueries({ queryKey: ["auth-status"] });
  }

  if (auth.isLoading) {
    return (
      <div className="grid min-h-screen place-items-center bg-slate-100 text-sm text-slate-600">
        <div className="flex items-center gap-2">
          <RefreshCw className="animate-spin" size={16} />
          正在检查控制台权限
        </div>
      </div>
    );
  }

  if (locked || auth.isError) {
    return (
      <div className="grid min-h-screen place-items-center bg-slate-100 p-4">
        <form autoComplete="on" onSubmit={submit} className="w-full max-w-sm rounded-md border bg-white p-4 shadow-sm">
          <div className="mb-4 flex items-center gap-2 text-sm font-semibold text-slate-900">
            <span className="grid size-8 place-items-center rounded-md bg-slate-900 text-white">
              <LockKeyhole size={16} />
            </span>
            账号控制台
          </div>
          <div className="grid gap-2">
            <input
              aria-hidden="true"
              autoComplete="username"
              className="sr-only"
              name="username"
              readOnly
              tabIndex={-1}
              type="text"
              value="cloak-account-console"
            />
            <label htmlFor="control-plane-access-key" className="grid gap-1 text-xs font-medium text-slate-600">
              访问密钥
              <span className="relative block">
                <Input
                  id="control-plane-access-key"
                  name="password"
                  type={showToken ? "text" : "password"}
                  autoComplete="current-password"
                  autoFocus
                  value={token}
                  onChange={(event) => {
                    setToken(event.target.value);
                    setAttempted(false);
                  }}
                  placeholder="输入 ADMIN_TOKEN 或 Open Key"
                  className="w-full pr-9"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 grid w-9 place-items-center rounded-r-md text-slate-500 transition hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-slate-500"
                  onClick={() => setShowToken((visible) => !visible)}
                  aria-label={showToken ? "隐藏访问密钥" : "显示访问密钥"}
                  aria-pressed={showToken}
                >
                  {showToken ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </span>
            </label>
            <label className="flex min-h-8 cursor-pointer items-center gap-2 text-xs text-slate-700">
              <input
                type="checkbox"
                checked={rememberToken}
                onChange={(event) => setRememberToken(event.target.checked)}
                className="size-4 rounded border-slate-300 accent-slate-900"
              />
              <span>记住访问密钥</span>
            </label>
            {rememberToken ? <p className="text-[11px] leading-4 text-amber-700">密钥会保存在当前浏览器中，公共设备请勿使用。</p> : null}
            {attempted ? <p className="text-xs text-rose-600">Token 未通过校验，请重新输入。</p> : null}
            {auth.isError ? <p className="text-xs text-rose-600">权限状态接口不可用，请检查 Worker API。</p> : null}
          </div>
          <div className="mt-4 flex items-center justify-between gap-2">
            <Button type="button" onClick={resetToken}>
              清空
            </Button>
            <Button type="submit" className="bg-slate-900 text-white hover:bg-slate-800">
              进入控制台
            </Button>
          </div>
          <p className="mt-3 text-[11px] leading-4 text-slate-500">
            当前模式：{modeLabel}。可使用 Worker Secret 中的 ADMIN_TOKEN，或设置页配置的 Open Key。
          </p>
        </form>
      </div>
    );
  }

  return <>{children}</>;
}
